import {
  Box,
  SimpleGrid,
  Button,
  useToast,
  Text,
  useColorModeValue,
  InputGroup,
  InputLeftElement,
  HStack,
  VStack,
  Flex,
  Select,
  Icon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Avatar,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Input,
  FormControl,
  FormLabel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Card,
  Badge,
} from "@chakra-ui/react";
import {
  MdSearch,
  MdMoreVert,
  MdGridView,
  MdList,
  MdChevronLeft,
  MdChevronRight,
  MdAccessTime,
  MdPersonOutline,
  MdClose,
  MdTrendingUp,
  MdCalendarToday,
  MdLocationOn,
  MdAdd,
  MdEdit,
  MdDelete,
} from "react-icons/md";
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  getUsers,
  checkToken,
  getUser,
  getAllPointagesToday,
  createSite,
  getSites,
  updateSite,
  deleteSite,
  assignUserToSite,
  getSiteUsers,
  removeUserFromSite,
  getAllUsersWithSites,
} from "../../../api/api";
import { useNavbarAction } from "contexts/NavbarActionContext";
import LoadingSpinner from "components/LoadingSpinner";
import StaffStatCard from "components/card/StaffStatCard";
import BulkSiteAssignmentModal from "./components/BulkSiteAssignmentModal";

// Badge de statut pour le pointage
const PointageStatusBadge = ({ status }) => {
  const statusConfig = {
    'en_garde': {
      bg: "#EDF7F1",
      border: "#CDE8D9",
      text: "#006133",
      label: "En garde"
    },
    'en garde': {
      bg: "#EDF7F1",
      border: "#CDE8D9",
      text: "#006133",
      label: "En garde"
    },
    'en_retard': {
      bg: "#FFF8E6",
      border: "#FFE0A3",
      text: "#B54708",
      label: "En retard"
    },
    'en retard': {
      bg: "#FFF8E6",
      border: "#FFE0A3",
      text: "#B54708",
      label: "En retard"
    },
    'absent': {
      bg: "#FDF2F2",
      border: "#FBD5D5",
      text: "#9B1C1C",
      label: "Absent"
    },
  };

  const normalizedStatus = status?.toLowerCase() || 'en_garde';
  const config = statusConfig[normalizedStatus] || {
    bg: "#F7FAFC",
    border: "#E2E8F0",
    text: "#4A5568",
    label: status
  };

  return (
    <HStack
      spacing={2}
      bg={config.bg}
      border="1px solid"
      borderColor={config.border}
      px={3}
      py={1}
      borderRadius="full"
      w="fit-content"
      align="center"
    >
      <Box w="8px" h="8px" borderRadius="full" bg={config.text} />
      <Text fontSize="13px" fontWeight="600" color={config.text}>
        {config.label}
      </Text>
    </HStack>
  );
};

// Composant pour afficher toutes les sessions d'un utilisateur
const SessionDisplay = ({ sessions }) => {
  if (!sessions || sessions.length === 0) {
    return (
      <HStack spacing={2} align="center">
        <Icon as={MdAccessTime} color="gray.400" w="16px" h="16px" />
        <Text fontSize="sm" color="gray.400">--:--</Text>
        <Box
          flex="1"
          h="2px"
          bg="gray.200"
          borderRadius="full"
          mx={2}
          minW="40px"
        />
        <Text fontSize="sm" color="gray.400">--:--</Text>
      </HStack>
    );
  }

  return (
    <VStack spacing={2} align="stretch">
      {sessions.map((session, index) => {
        const hasStart = session.start && session.start !== '--:--';
        const hasEnd = session.end && session.end !== '--:--';

        return (
          <HStack key={index} spacing={2} align="center">
            <Icon as={MdAccessTime} color="gray.400" w="16px" h="16px" />
            <Text fontSize="sm" color={hasStart ? "gray.700" : "gray.400"}>
              {session.start || '--:--'}
            </Text>
            <Box
              flex="1"
              h="2px"
              bg={hasStart && hasEnd ? "green.300" : "gray.200"}
              borderRadius="full"
              mx={2}
              minW="40px"
            />
            <Text fontSize="sm" color={hasEnd ? "gray.700" : "gray.400"}>
              {session.end || '--:--'}
            </Text>
          </HStack>
        );
      })}
    </VStack>
  );
};

export default function PointageSuivi() {
  const navigate = useNavigate();
  const { navbarHeight, showStats, setShowStats } = useNavbarAction();
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const textColor = useColorModeValue('secondaryGray.900', 'white');

  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewMode, setViewMode] = useState("list");
  const [showSiteManagement, setShowSiteManagement] = useState(false); // Afficher/masquer gestion des sites
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]); // Date du jour par défaut
  const [tempDate, setTempDate] = useState(new Date().toISOString().split('T')[0]); // Date temporaire pour le picker

  // États pour la gestion des sites
  const [sites, setSites] = useState([]);
  const [siteSearchQuery, setSiteSearchQuery] = useState("");
  const [selectedSite, setSelectedSite] = useState(null);
  const [siteUsers, setSiteUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [siteFormData, setSiteFormData] = useState({
    SiteName: "",
    Address: "",
    Latitude: 0,
    Longitude: 0,
    RadiusMeters: 150,
  });
  const [isSiteEditMode, setIsSiteEditMode] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [assignmentStartDate, setAssignmentStartDate] = useState(new Date().toISOString().split('T')[0]);

  const {
    isOpen: isAddSiteOpen,
    onOpen: onAddSiteOpen,
    onClose: onAddSiteClose,
  } = useDisclosure();

  const {
    isOpen: isSiteDetailsOpen,
    onOpen: onSiteDetailsOpen,
    onClose: onSiteDetailsClose,
  } = useDisclosure();

  const {
    isOpen: isAssignUserOpen,
    onOpen: onAssignUserOpen,
    onClose: onAssignUserClose,
  } = useDisclosure();

  const {
    isOpen: isBulkAssignOpen,
    onOpen: onBulkAssignOpen,
    onClose: onBulkAssignClose,
  } = useDisclosure();

  const bgColor = useColorModeValue('gray.50', 'gray.800');
  const cardBg = useColorModeValue('white', 'gray.700');
  const theadBg = useColorModeValue("gray.50", "gray.600");

  useEffect(() => {
    const checkTokenAndUser = async () => {
      try {
        const check = await checkToken();
        if (!check.data.valid) {
          navigate('/auth/sign-in');
          return;
        }

        const user = await getUser();
        const userRole = user.rôle || user['rÃ´le'] || user.role;
        if (
          userRole !== 'admin' &&
          userRole !== 'régulateur' &&
          userRole !== 'rÃ©gulateur' &&
          userRole !== 'médecin regulateur' &&
          userRole !== 'medecin regulateur'
        ) {
          navigate('/auth/sign-in');
          return;
        }

        fetchData();
      } catch (error) {
        console.error("Error:", error);
        navigate('/auth/sign-in');
      }
    };
    checkTokenAndUser();
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, []); // Charger une seule fois au démarrage

  // Fonction pour rechercher avec la date sélectionnée
  const handleDateSearch = () => {
    setSelectedDate(tempDate);
    fetchData(tempDate); // Passer la nouvelle date directement
  };

  // Fonction pour formater l'heure depuis une date
  const formatTime = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  };

  // ===== FONCTIONS POUR LA GESTION DES SITES =====
  const fetchSites = async () => {
    try {
      const res = await getSites();
      setSites(res.data);
    } catch (err) {
      console.error("Error fetching sites:", err);
      toast({ title: "Erreur lors du chargement des sites", status: "error", isClosable: true });
    }
  };

  const fetchSiteUsers = async (siteId) => {
    try {
      const res = await getSiteUsers(siteId);
      setSiteUsers(res.data);
    } catch (err) {
      console.error("Error fetching site users:", err);
      toast({ title: "Erreur", status: "error", isClosable: true });
    }
  };

  const handleAddSite = async () => {
    try {
      if (!siteFormData.SiteName || !siteFormData.Address) {
        toast({ title: "Remplissez les champs obligatoires", status: "warning", isClosable: true });
        return;
      }

      if (isSiteEditMode) {
        await updateSite(selectedSite.SiteID, siteFormData);
        toast({ title: "Site mis à jour", status: "success", isClosable: true });
      } else {
        await createSite(siteFormData);
        toast({ title: "Site créé", status: "success", isClosable: true });
      }

      resetSiteForm();
      onAddSiteClose();
      fetchSites();
    } catch (err) {
      console.error("Error saving site:", err);
      toast({ title: "Erreur", status: "error", isClosable: true });
    }
  };

  const handleDeleteSite = async (siteId) => {
    try {
      await deleteSite(siteId);
      toast({ title: "Site supprimé", status: "success", isClosable: true });
      fetchSites();
      onSiteDetailsClose();
    } catch (err) {
      console.error("Error deleting site:", err);
      toast({ title: "Erreur", status: "error", isClosable: true });
    }
  };

  const handleAssignUser = async () => {
    try {
      if (!selectedUserId || !assignmentStartDate) {
        toast({ title: "Sélectionnez un utilisateur et une date", status: "warning", isClosable: true });
        return;
      }

      await assignUserToSite({
        UserID: selectedUserId,
        SiteID: selectedSite.SiteID,
        StartDate: assignmentStartDate,
        EndDate: null,
      });

      toast({ title: "Utilisateur assigné", status: "success", isClosable: true });
      setSelectedUserId("");
      setAssignmentStartDate(new Date().toISOString().split('T')[0]);
      onAssignUserClose();
      await fetchSiteUsers(selectedSite.SiteID);
    } catch (err) {
      console.error("Error assigning user:", err);
      toast({ title: "Erreur", description: err.response?.data?.error, status: "error", isClosable: true });
    }
  };

  const handleRemoveUserFromSite = async (userSiteId) => {
    try {
      await removeUserFromSite(userSiteId);
      toast({ title: "Utilisateur retiré", status: "success", isClosable: true });
      await fetchSiteUsers(selectedSite.SiteID);
    } catch (err) {
      console.error("Error removing user:", err);
      toast({ title: "Erreur", status: "error", isClosable: true });
    }
  };

  const resetSiteForm = () => {
    setSiteFormData({
      SiteName: "",
      Address: "",
      Latitude: 0,
      Longitude: 0,
      RadiusMeters: 150,
    });
    setIsSiteEditMode(false);
    setSelectedSite(null);
  };

  // Charger les sites au démarrage
  useEffect(() => {
    fetchSites();
    // Charger tous les utilisateurs pour l'assignation
    const loadUsers = async () => {
      try {
        const res = await getAllUsersWithSites();
        const formattedUsers = res.data.map(u => ({
          ...u,
          site: u.SiteName ? { id: u.SiteID, name: u.SiteName } : null
        }));
        setAllUsers(formattedUsers);
      } catch (err) {
        console.error("Error loading users:", err);
      }
    };
    loadUsers();
  }, []);

  // Fonction pour traiter les données de pointage réelles
  const processPointageData = (users, pointages) => {
    // Grouper les pointages par user_id
    const pointagesByUser = {};
    pointages.forEach(p => {
      if (!pointagesByUser[p.user_id]) {
        pointagesByUser[p.user_id] = [];
      }
      pointagesByUser[p.user_id].push(p);
    });

    return users
      .filter(user => ['infirmier', 'ambulancier'].includes(user.rôle?.toLowerCase()))
      .map(user => {
        const userPointages = pointagesByUser[user.id] || [];

        // Trier les pointages par date
        userPointages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

        // Collecter seulement la dernière session
        let sessions = [];
        let currentSession = null;
        let totalMinutes = 0;
        let currentSessionStart = null;

        userPointages.forEach(p => {
          const time = new Date(p.created_at);
          const formattedTime = formatTime(p.created_at);

          if (p.type === 'entrée') {
            // Créer une nouvelle session pour chaque entrée
            currentSession = {
              start: formattedTime,
              end: null,
              startTime: time
            };
            currentSessionStart = time;
          } else if (p.type === 'sortie') {
            // Fermer la session en cours si elle existe
            if (currentSession) {
              currentSession.end = formattedTime;
              // Calculer les minutes travaillées
              totalMinutes += Math.floor((time - currentSessionStart) / 60000);
            }
          } else if (p.type === 'pause') {
            // Pause = fin temporaire de session
            if (currentSessionStart) {
              totalMinutes += Math.floor((time - currentSessionStart) / 60000);
              currentSessionStart = null;
            }
          } else if (p.type === 'retour_pause') {
            // Retour de pause = reprise
            currentSessionStart = time;
          }
        });

        // Ajouter seulement la dernière session (complète ou non)
        if (currentSession && currentSession.start) {
          sessions.push(currentSession);
        }

        const totalHours = Math.floor(totalMinutes / 60);
        const remainingMinutes = totalMinutes % 60;
        const heuresTotales = totalMinutes > 0 ? `${totalHours}h ${remainingMinutes}m` : '0h';

        // Déterminer le statut
        let statut = 'absent';
        const hasPointage = userPointages.length > 0;
        const firstPointage = userPointages[0];

        if (hasPointage) {
          // Vérifier si en retard (entrée après 9h00)
          if (firstPointage && firstPointage.type === 'entrée') {
            const entryTime = new Date(firstPointage.created_at);
            const isLate = entryTime.getHours() > 9 || (entryTime.getHours() === 9 && entryTime.getMinutes() > 0);
            statut = isLate ? 'en_retard' : 'en_garde';
          } else {
            statut = 'en_garde';
          }
        }

        return {
          id: user.id,
          nom: user.nom || '',
          prenom: user.prenom || '',
          photo: user.photo || "https://img.freepik.com/vecteurs-premium/icones-utilisateur-comprend-icones-utilisateur-symboles-icones-personnes-elements-conception-graphique-qualite-superieure_981536-526.jpg",
          rôle: user.rôle || '',
          sessions: sessions,
          heuresTotales,
          totalMinutes,
          statut,
          site: user.SiteName ? { id: user.SiteID, name: user.SiteName } : null,
          pointages: userPointages // Garder les pointages bruts pour référence
        };
      });
  };

  const fetchData = async (dateToFetch) => {
    try {
      setLoading(true);

      // Utiliser la date passée en paramètre ou selectedDate
      const date = dateToFetch || selectedDate;

      // Charger les utilisateurs et les pointages en parallèle
      const [usersWithSitesRes, pointagesRes] = await Promise.all([
        getAllUsersWithSites(),
        getAllPointagesToday(date) // Passer la date sélectionnée
      ]);

      const users = usersWithSitesRes.data || [];
      const pointages = pointagesRes.data?.data || [];

      console.log('Date sélectionnée:', date);
      console.log('Pointages récupérés:', pointages);

      const pointageData = processPointageData(users, pointages);
      setTableData(pointageData);
    } catch (err) {
      console.error("Fetch error:", err);
      toast({ title: "Erreur lors du chargement des données", status: "error", isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  // Calcul des statistiques
  const stats = useMemo(() => {
    const enGarde = tableData.filter(user => user.statut === 'en_garde').length;
    const enRetard = tableData.filter(user => user.statut === 'en_retard').length;
    const absents = tableData.filter(user => user.statut === 'absent').length;
    const totalMinutes = tableData.reduce((acc, user) => acc + (user.totalMinutes || 0), 0);
    const totalHeures = Math.floor(totalMinutes / 60);

    return {
      enGarde,
      enRetard,
      absents,
      totalHeures: `${totalHeures}h`,
    };
  }, [tableData]);

  // Compteurs par catégorie
  const categoryCounts = useMemo(() => {
    return {
      all: tableData.length,
      infirmier: tableData.filter(u => u.rôle?.toLowerCase() === 'infirmier').length,
      ambulancier: tableData.filter(u => u.rôle?.toLowerCase() === 'ambulancier').length,
    };
  }, [tableData]);

  const filteredData = useMemo(() => {
    let filtered = tableData;

    // Filtre par recherche
    if (searchQuery) {
      filtered = filtered.filter((row) => {
        const searchFields = [
          row.nom,
          row.prenom,
          row.rôle,
        ];
        return searchFields.some(field =>
          field && field.toLowerCase().includes(searchQuery.toLowerCase())
        );
      });
    }

    // Filtre par catégorie
    if (categoryFilter !== "all") {
      filtered = filtered.filter((row) => {
        const role = row.rôle?.toLowerCase() || '';
        return role === categoryFilter.toLowerCase();
      });
    }

    // Filtre par statut
    if (statusFilter !== "all") {
      filtered = filtered.filter((row) => {
        const statut = row.statut?.toLowerCase() || '';
        return statut === statusFilter.toLowerCase();
      });
    }

    return filtered;
  }, [tableData, searchQuery, categoryFilter, statusFilter]);

  // Pagination
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  if (loading) {
    const topPadding = navbarHeight > 0 ? `${navbarHeight}px` : '200px';
    return (
      <Box minH="100vh" bg={bgColor} px={6} pb={6} pt={0}>
        <Box maxW="9xl" mx="auto" pt={topPadding} transition="padding-top 0.3s ease">
          <LoadingSpinner message="Chargement des pointages..." size="lg" fullHeight />
        </Box>
      </Box>
    );
  }

  const topPadding = navbarHeight > 0 ? `${navbarHeight}px` : '200px';

  return (
    <Box minH="100vh" bg={bgColor} px={6} pb={6} pt={0}>
      <Box maxW="9xl" mx="auto" pt={topPadding} transition="padding-top 0.3s ease">
        <VStack spacing={6} align="stretch">

          {/* Statistics Section */}
          {showStats && (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={6}>
              <StaffStatCard
                title="En garde"
                value={stats.enGarde}
                subtitle={`Personnel présent le ${new Date(selectedDate).toLocaleDateString('fr-FR')}`}
                icon={MdPersonOutline}
                iconColor="#17B26A"
                isPositive={true}
              />
              <StaffStatCard
                title="En retard"
                value={String(stats.enRetard).padStart(2, '0')}
                subtitle={`Retard signalé le ${new Date(selectedDate).toLocaleDateString('fr-FR')}`}
                icon={MdAccessTime}
                iconColor="#F79009"
                isPositive={false}
              />
              <StaffStatCard
                title="Absents"
                value={String(stats.absents).padStart(2, '0')}
                subtitle={`Non pointés le ${new Date(selectedDate).toLocaleDateString('fr-FR')}`}
                icon={MdClose}
                iconColor="#F04438"
                isPositive={false}
              />
              <StaffStatCard
                title="Total heures"
                value={stats.totalHeures}
                subtitle={`Heures cumulées le ${new Date(selectedDate).toLocaleDateString('fr-FR')}`}
                icon={MdTrendingUp}
                iconColor="#459CE9"
                isPositive={true}
              />
            </SimpleGrid>
          )}

          {/* Main Content */}
          <Box bg={cardBg} borderRadius="12px" p={4} mb={4}>
            <VStack spacing={4} align="stretch">
              {/* Category Filters */}
              <HStack spacing={0} flexWrap="wrap" justify="space-between">
                <HStack spacing={0} flexWrap="wrap">
                  <Button
                    size="sm"
                    variant="ghost"
                    fontWeight={!showSiteManagement && categoryFilter === "all" ? "600" : "400"}
                    color={!showSiteManagement && categoryFilter === "all" ? "blue.600" : "gray.600"}
                    borderBottom={!showSiteManagement && categoryFilter === "all" ? "2px solid" : "none"}
                    borderColor="blue.600"
                    borderRadius="0"
                    onClick={() => {
                      setShowSiteManagement(false);
                      setCategoryFilter("all");
                    }}
                    px={4}
                  >
                    Tous <Text as="span" ml={2} bg={!showSiteManagement && categoryFilter === "all" ? "blue.100" : "gray.100"} px={2} py={0.5} borderRadius="md" fontSize="xs">{categoryCounts.all}</Text>
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    fontWeight={!showSiteManagement && categoryFilter === "infirmier" ? "600" : "400"}
                    color={!showSiteManagement && categoryFilter === "infirmier" ? "blue.600" : "gray.600"}
                    borderBottom={!showSiteManagement && categoryFilter === "infirmier" ? "2px solid" : "none"}
                    borderColor="blue.600"
                    borderRadius="0"
                    onClick={() => {
                      setShowSiteManagement(false);
                      setCategoryFilter("infirmier");
                    }}
                    px={4}
                  >
                    Infirmier <Text as="span" ml={2} bg={!showSiteManagement && categoryFilter === "infirmier" ? "blue.100" : "gray.100"} px={2} py={0.5} borderRadius="md" fontSize="xs">{categoryCounts.infirmier}</Text>
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    fontWeight={!showSiteManagement && categoryFilter === "ambulancier" ? "600" : "400"}
                    color={!showSiteManagement && categoryFilter === "ambulancier" ? "blue.600" : "gray.600"}
                    borderBottom={!showSiteManagement && categoryFilter === "ambulancier" ? "2px solid" : "none"}
                    borderColor="blue.600"
                    borderRadius="0"
                    onClick={() => {
                      setShowSiteManagement(false);
                      setCategoryFilter("ambulancier");
                    }}
                    px={4}
                  >
                    Ambulancier <Text as="span" ml={2} bg={!showSiteManagement && categoryFilter === "ambulancier" ? "blue.100" : "gray.100"} px={2} py={0.5} borderRadius="md" fontSize="xs">{categoryCounts.ambulancier}</Text>
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    fontWeight={showSiteManagement ? "600" : "400"}
                    color={showSiteManagement ? "blue.600" : "gray.600"}
                    borderBottom={showSiteManagement ? "2px solid" : "none"}
                    borderColor="blue.600"
                    borderRadius="0"
                    onClick={() => setShowSiteManagement(true)}
                    px={4}
                  >
                    Gestion des Sites
                  </Button>
                </HStack>
              </HStack>

              {/* Search and Controls */}
              {!showSiteManagement && (
                <Flex gap={4} align="center" flexWrap="wrap" justify="space-between">
                  <InputGroup maxW="400px" flex="1" minW="250px">
                    <InputLeftElement pointerEvents="none">
                      <Icon as={MdSearch} color="gray.400" />
                    </InputLeftElement>
                    <Input
                      placeholder="Recherche..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setCurrentPage(1);
                      }}
                      bg="white"
                      borderRadius="md"
                    />
                  </InputGroup>

                  <HStack spacing={4}>
                    <Select
                      value={categoryFilter}
                      onChange={(e) => {
                        setCategoryFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                      maxW="150px"
                      size="sm"
                      bg="white"
                    >
                      <option value="all">Rôle: Tous</option>
                      <option value="médecin">Médecin</option>
                      <option value="infirmier">Infirmier</option>
                      <option value="ambulancier">Ambulancier</option>
                    </Select>

                    <Select
                      value={statusFilter}
                      onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                      maxW="150px"
                      size="sm"
                      bg="white"
                    >
                      <option value="all">Statut: Tous</option>
                      <option value="en_garde">En garde</option>
                      <option value="en_retard">En retard</option>
                      <option value="absent">Absent</option>
                    </Select>

                    {/* Date Picker with Search Button */}
                    <HStack spacing={2} bg="white" p={1} borderRadius="md" border="1px solid" borderColor="gray.200">
                      <Icon as={MdCalendarToday} color="gray.500" ml={2} />
                      <Input
                        type="date"
                        value={tempDate}
                        onChange={(e) => setTempDate(e.target.value)}
                        size="sm"
                        bg="white"
                        border="none"
                        maxW="150px"
                        _focus={{ boxShadow: "none" }}
                      />
                      <Button
                        size="sm"
                        colorScheme="blue"
                        onClick={handleDateSearch}
                        px={4}
                      >
                        Rechercher
                      </Button>
                    </HStack>

                    <HStack spacing={2}>
                      <IconButton
                        icon={<MdGridView />}
                        aria-label="Vue grille"
                        size="sm"
                        variant={viewMode === "grid" ? "solid" : "outline"}
                        colorScheme={viewMode === "grid" ? "blue" : "gray"}
                        onClick={() => setViewMode("grid")}
                      />
                      <IconButton
                        icon={<MdList />}
                        aria-label="Vue liste"
                        size="sm"
                        variant={viewMode === "list" ? "solid" : "outline"}
                        colorScheme={viewMode === "list" ? "blue" : "gray"}
                        onClick={() => setViewMode("list")}
                      />
                    </HStack>
                  </HStack>
                </Flex>
              )}
            </VStack>
          </Box>

          {/* Table Section */}
          <Box bg={cardBg} borderRadius="12px" p={6}>
            {showSiteManagement && (
              <Flex justify="space-between" align="center" mb={4}>
                <InputGroup maxW="300px">
                  <InputLeftElement pointerEvents="none">
                    <MdSearch />
                  </InputLeftElement>
                  <Input
                    placeholder="Rechercher un site..."
                    value={siteSearchQuery}
                    onChange={(e) => setSiteSearchQuery(e.target.value)}
                  />
                </InputGroup>
                <HStack spacing={4}>
                  <Button leftIcon={<MdAdd />} colorScheme="blue" onClick={() => {
                    resetSiteForm();
                    setIsSiteEditMode(false);
                    onAddSiteOpen();
                  }}>
                    Nouveau Site
                  </Button>
                  <Button
                    leftIcon={<MdPersonOutline />}
                    colorScheme="teal"
                    variant="outline"
                    onClick={onBulkAssignOpen}
                  >
                    Assigner des Utilisateurs
                  </Button>
                </HStack>
              </Flex>
            )}
            <TableContainer border="1px solid" borderColor="gray.200" borderRadius="12px" overflowX="auto">
              <Table variant="simple" minW="1200px" sx={{
                'th, td': {
                  borderRight: '1px solid',
                  borderBottom: '1px solid',
                  borderColor: 'gray.200',
                  whiteSpace: 'nowrap'
                },
                'th:last-child, td:last-child': { borderRight: 'none' },
                'tr:last-child td': { borderBottom: 'none' }
              }}>
                <Thead>
                  <Tr bg="gray.50">
                    {!showSiteManagement ? (
                      <>
                        <Th w="40px" px={2}>
                          <input type="checkbox" />
                        </Th>
                        <Th minW="220px" cursor="pointer">
                          <HStack>
                            <Text>Employee Name</Text>
                            <Icon as={MdChevronLeft} transform="rotate(-90deg)" />
                          </HStack>
                        </Th>
                        <Th w="130px" cursor="pointer">
                          <HStack>
                            <Text>Rôle</Text>
                            <Icon as={MdChevronLeft} transform="rotate(-90deg)" />
                          </HStack>
                        </Th>
                        <Th w="160px" cursor="pointer">
                          <HStack>
                            <Text>Site</Text>
                            <Icon as={MdChevronLeft} transform="rotate(-90deg)" />
                          </HStack>
                        </Th>
                        <Th minW="220px" cursor="pointer" >
                          <HStack>
                            <Text>Session </Text>
                            <Icon as={MdChevronLeft} transform="rotate(-90deg)" />
                          </HStack>
                        </Th>
                        <Th w="130px" cursor="pointer">
                          <HStack>
                            <Text>Heures Totales</Text>
                            <Icon as={MdChevronLeft} transform="rotate(-90deg)" />
                          </HStack>
                        </Th>
                        <Th w="60px">Actions</Th>
                      </>
                    ) : (
                      <>
                        <Th>Nom du Site</Th>
                        <Th>Adresse</Th>
                        <Th>Rayon (m)</Th>
                        <Th>Actions</Th>
                      </>
                    )}
                  </Tr>
                </Thead>
                <Tbody>
                  {!showSiteManagement ? (
                    paginatedData.length === 0 ? (
                      <Tr>
                        <Td colSpan={7} textAlign="center" py={8}>
                          <Text color="gray.500">Aucun pointage trouvé</Text>
                        </Td>
                      </Tr>
                    ) : (
                      paginatedData.map((row) => {
                        const fullName = `${row.prenom || ''} ${row.nom || ''}`.trim() || 'N/A';
                        const roleFormatted = row.rôle ? row.rôle.charAt(0).toUpperCase() + row.rôle.slice(1) : '---';

                        return (
                          <Tr key={row.id} _hover={{ bg: "gray.50" }}>
                            <Td px={2}>
                              <input type="checkbox" />
                            </Td>
                            <Td>
                              <HStack spacing={3}>
                                <Avatar
                                  size="sm"
                                  src={row.photo}
                                  name={fullName}
                                  borderRadius="8px"
                                />
                                <Text fontWeight="medium">{fullName}</Text>
                              </HStack>
                            </Td>
                            <Td>
                              <Text fontSize="sm" color="gray.700">{roleFormatted}</Text>
                            </Td>
                            <Td>
                              {row.site ? (
                                <Badge colorScheme="teal" variant="subtle" display="flex" alignItems="center" w="fit-content" gap={1}>
                                  <MdLocationOn size={12} />
                                  {row.site.name}
                                </Badge>
                              ) : (
                                <Text fontSize="xs" color="gray.400">Non assigné</Text>
                              )}
                            </Td>
                            <Td>
                              <SessionDisplay sessions={row.sessions} />
                            </Td>
                            <Td>
                              <Text fontWeight="600" color="gray.700">{row.heuresTotales}</Text>
                            </Td>
                            <Td>
                              <Menu>
                                <MenuButton
                                  as={IconButton}
                                  icon={<MdMoreVert />}
                                  variant="ghost"
                                  size="sm"
                                  aria-label="Actions"
                                />
                                <MenuList>
                                  <MenuItem isDisabled color="gray.400">Voir détails (à venir)</MenuItem>
                                  <MenuItem isDisabled color="gray.400">Historique (à venir)</MenuItem>
                                </MenuList>
                              </Menu>
                            </Td>
                          </Tr>
                        );
                      })
                    )
                  ) : (
                    sites.filter(site => site.SiteName.toLowerCase().includes(siteSearchQuery.toLowerCase())).length === 0 ? (
                      <Tr>
                        <Td colSpan={4} textAlign="center" py={8}>
                          <Text color="gray.500">Aucun site</Text>
                        </Td>
                      </Tr>
                    ) : (
                      sites.filter(site => site.SiteName.toLowerCase().includes(siteSearchQuery.toLowerCase())).map((site) => (
                        <Tr key={site.SiteID} _hover={{ bg: "gray.50" }}>
                          <Td fontWeight="medium">{site.SiteName}</Td>
                          <Td fontSize="sm" color="gray.600">{site.Address}</Td>
                          <Td>{site.RadiusMeters || 150}m</Td>
                          <Td>
                            <Menu>
                              <MenuButton as={IconButton} icon={<MdMoreVert />} variant="ghost" size="sm" />
                              <MenuList>
                                <MenuItem icon={<MdLocationOn />} onClick={() => {
                                  setSelectedSite(site);
                                  setSiteFormData(site);
                                  fetchSiteUsers(site.SiteID);
                                  onSiteDetailsOpen();
                                }}>
                                  Voir Détails
                                </MenuItem>
                                <MenuItem icon={<MdEdit />} onClick={() => {
                                  setSelectedSite(site);
                                  setSiteFormData(site);
                                  setIsSiteEditMode(true);
                                  onAddSiteOpen();
                                }}>
                                  Modifier
                                </MenuItem>
                                <MenuItem icon={<MdDelete />} color="red.500" onClick={() => handleDeleteSite(site.SiteID)}>
                                  Supprimer
                                </MenuItem>
                              </MenuList>
                            </Menu>
                          </Td>
                        </Tr>
                      ))
                    )
                  )}
                </Tbody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {!showSiteManagement && paginatedData.length > 0 && (
              <Flex justify="space-between" align="center" p={4} borderTop="1px solid" borderColor="gray.200" mt={4}>
                <HStack spacing={2}>
                  <Text fontSize="sm" color="gray.600">Afficher</Text>
                  <Select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    w="80px"
                    size="sm"
                    bg="white"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </Select>
                  <Text fontSize="sm" color="gray.600">par page</Text>
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  {startIndex + 1} à {Math.min(endIndex, filteredData.length)} sur {filteredData.length}
                </Text>
                <HStack spacing={2}>
                  <IconButton
                    icon={<MdChevronLeft />}
                    aria-label="Page précédente"
                    size="sm"
                    variant="ghost"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    isDisabled={currentPage === 1}
                  />
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        size="sm"
                        variant={currentPage === pageNum ? "solid" : "ghost"}
                        colorScheme={currentPage === pageNum ? "blue" : "gray"}
                        onClick={() => setCurrentPage(pageNum)}
                        minW="32px"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                  <IconButton
                    icon={<MdChevronRight />}
                    aria-label="Page suivante"
                    size="sm"
                    variant="ghost"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    isDisabled={currentPage === totalPages}
                  />
                </HStack>
              </Flex>
            )}
          </Box>
        </VStack>
      </Box>

      {/* Modals pour la gestion des sites */}
      <Modal isOpen={isAddSiteOpen} onClose={onAddSiteClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{isSiteEditMode ? "Modifier Site" : "Créer un Nouveau Site"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Nom du Site</FormLabel>
                <Input
                  placeholder="Ex: Hôpital Universitaire"
                  value={siteFormData.SiteName}
                  onChange={(e) => setSiteFormData({ ...siteFormData, SiteName: e.target.value })}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Adresse</FormLabel>
                <Input
                  placeholder="Ex: 123 Rue de l'Hôpital"
                  value={siteFormData.Address}
                  onChange={(e) => setSiteFormData({ ...siteFormData, Address: e.target.value })}
                />
              </FormControl>
              <HStack spacing={4} w="full">
                <FormControl>
                  <FormLabel>Latitude</FormLabel>
                  <Input
                    type="number"
                    step="0.00000001"
                    value={siteFormData.Latitude}
                    onChange={(e) => setSiteFormData({ ...siteFormData, Latitude: parseFloat(e.target.value) })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Longitude</FormLabel>
                  <Input
                    type="number"
                    step="0.00000001"
                    value={siteFormData.Longitude}
                    onChange={(e) => setSiteFormData({ ...siteFormData, Longitude: parseFloat(e.target.value) })}
                  />
                </FormControl>
              </HStack>
              <FormControl>
                <FormLabel>Rayon (mètres)</FormLabel>
                <Input
                  type="number"
                  value={siteFormData.RadiusMeters}
                  onChange={(e) => setSiteFormData({ ...siteFormData, RadiusMeters: parseInt(e.target.value) })}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button variant="ghost" onClick={onAddSiteClose}>Annuler</Button>
              <Button colorScheme="blue" onClick={handleAddSite}>
                {isSiteEditMode ? "Mettre à Jour" : "Créer"}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal Détails Site */}
      {selectedSite && (
        <Modal isOpen={isSiteDetailsOpen} onClose={onSiteDetailsClose} size="lg">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{selectedSite.SiteName}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={6} align="start" w="full">
                <Box w="full">
                  <Text fontWeight="bold" mb={2}>Informations</Text>
                  <VStack align="start" spacing={2} fontSize="sm">
                    <HStack>
                      <Text fontWeight="medium">Adresse:</Text>
                      <Text color="gray.600">{selectedSite.Address}</Text>
                    </HStack>
                    <HStack>
                      <Text fontWeight="medium">Coordonnées:</Text>
                      <Text color="gray.600">{selectedSite.Latitude}, {selectedSite.Longitude}</Text>
                    </HStack>
                    <HStack>
                      <Text fontWeight="medium">Rayon:</Text>
                      <Text color="gray.600">{selectedSite.RadiusMeters}m</Text>
                    </HStack>
                  </VStack>
                </Box>

                <Box w="full">
                  <Flex justify="space-between" align="center" mb={2}>
                    <Text fontWeight="bold">Utilisateurs Assignés</Text>
                    <Button size="sm" colorScheme="blue" onClick={onAssignUserOpen}>Assigner</Button>
                  </Flex>

                  {siteUsers.length === 0 ? (
                    <Text fontSize="sm" color="gray.500">Aucun utilisateur assigné</Text>
                  ) : (
                    <TableContainer>
                      <Table variant="simple" size="sm">
                        <Thead>
                          <Tr>
                            <Th>Utilisateur</Th>
                            <Th>Date Début</Th>
                            <Th>Actions</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {siteUsers.map((user) => (
                            <Tr key={user.UserSiteID}>
                              <Td>{user.prenom} {user.nom}</Td>
                              <Td fontSize="xs">{new Date(user.StartDate).toLocaleDateString("fr-FR")}</Td>
                              <Td>
                                <IconButton
                                  icon={<MdDelete />}
                                  size="sm"
                                  colorScheme="red"
                                  variant="ghost"
                                  onClick={() => handleRemoveUserFromSite(user.UserSiteID)}
                                />
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <HStack spacing={3}>
                <Button
                  colorScheme="blue"
                  variant="outline"
                  onClick={() => {
                    setSiteFormData(selectedSite);
                    setIsSiteEditMode(true);
                    onSiteDetailsClose();
                    onAddSiteOpen();
                  }}
                >
                  Modifier
                </Button>
                <Button variant="ghost" onClick={onSiteDetailsClose}>Fermer</Button>
              </HStack>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* Modal Assigner Utilisateur */}
      {selectedSite && (
        <Modal isOpen={isAssignUserOpen} onClose={onAssignUserClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Assigner Utilisateur à {selectedSite.SiteName}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4}>
                <FormControl>
                  <FormLabel>Utilisateur</FormLabel>
                  <Select
                    placeholder="Sélectionner un utilisateur"
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                  >
                    {allUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.prenom} {user.nom}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                {/* Date de Début is automatic */}
              </VStack>
            </ModalBody>

            <ModalFooter>
              <HStack spacing={3}>
                <Button variant="ghost" onClick={onAssignUserClose}>Annuler</Button>
                <Button colorScheme="blue" onClick={handleAssignUser}>Assigner</Button>
              </HStack>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
      <BulkSiteAssignmentModal
        isOpen={isBulkAssignOpen}
        onClose={onBulkAssignClose}
        sites={sites}
        allUsers={allUsers}
        onSuccess={() => {
          fetchSites();
          // Optionnel: rafraîchir d'autres données si besoin
        }}
      />
    </Box>
  );
}
